import { Box, SxProps, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  colorLandingBlue,
  colorLandingBrand,
  colorLandingInk,
  colorLandingLine,
  colorLandingMuted,
  colorLandingOffer,
  colorLandingSurface,
  colorLandingText,
  fontLandingDisplay,
  radiusLandingCard,
  shadowLandingRaised,
  colorLandingBrowserChrome,
  colorLandingBrowserDotGreen,
  colorLandingBrowserDotRed,
  colorLandingBrowserDotYellow,
  colorLandingCream,
} from "src/design/tokens";
import restaurantInterior from "src/assets/landing/restaurant-interior.jpg";

interface BookingPageMockProps {
  sx?: SxProps;
}

const WEEKDAY_INITIALS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// May 2026 starts on a Friday: five blank cells, then 31 days. Days carrying a
// deal or an event are dotted, matching the legend above the calendar.
const LEADING_BLANKS = 5;
const DAYS_IN_MONTH = 31;
const OFFER_DAYS = [8, 22];
const EVENT_DAYS = [15, 29];
const TODAY = 31;

const DayDot = ({ color }: { color: string }) => (
  <Box
    sx={{
      position: "absolute",
      bottom: 1,
      left: "50%",
      transform: "translateX(-50%)",
      width: 3.5,
      height: 3.5,
      borderRadius: "50%",
      backgroundColor: color,
    }}
  />
);

/**
 * The restaurant's own hosted booking page, framed in a browser window. Second
 * of the two customer-facing entry points shown in the ecosystem flow.
 */
export const BookingPageMock = ({ sx }: BookingPageMockProps) => {
  const { t } = useTranslation();

  const legend = [
    { color: colorLandingBrand, labelKey: "landingPage.ecosystem.bookingPage.legendAvailable" },
    { color: colorLandingOffer, labelKey: "landingPage.ecosystem.bookingPage.legendOffer" },
    { color: colorLandingBlue, labelKey: "landingPage.ecosystem.bookingPage.legendEvents" },
  ];

  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 200,
        backgroundColor: colorLandingSurface,
        border: `1px solid ${colorLandingLine}`,
        borderRadius: radiusLandingCard,
        boxShadow: shadowLandingRaised,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        textAlign: "left",
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.625,
          backgroundColor: colorLandingBrowserChrome,
          px: 1.25,
          py: 1,
        }}
      >
        {[
          colorLandingBrowserDotRed,
          colorLandingBrowserDotYellow,
          colorLandingBrowserDotGreen,
        ].map((dot) => (
          <Box
            key={dot}
            sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: dot }}
          />
        ))}
        <Typography
          sx={{
            ml: 0.75,
            flex: 1,
            fontSize: 8.5,
            color: colorLandingMuted,
            backgroundColor: colorLandingSurface,
            borderRadius: "6px",
            px: 1,
            py: 0.375,
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
        >
          {t("landingPage.ecosystem.bookingPage.url")}
        </Typography>
      </Box>

      <Box
        sx={{
          backgroundColor: colorLandingInk,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 1,
          py: 0.875,
          position: "relative",
        }}
      >
        <Typography
          sx={{
            fontFamily: fontLandingDisplay,
            color: colorLandingCream,
            fontSize: 14,
            letterSpacing: "0.3px",
          }}
        >
          L'addition
        </Typography>
        <Box
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "2px",
            width: 13,
            height: 13,
          }}
        >
          {[0, 1, 2, 3].map((cell) => (
            <Box
              key={cell}
              sx={{ backgroundColor: colorLandingCream, borderRadius: "2px", opacity: 0.8 }}
            />
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          height: 38,
          backgroundImage: `url(${restaurantInterior})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <Box sx={{ px: 1.375, pt: 1.125, pb: 1.375 }}>
        <Typography
          sx={{ fontFamily: fontLandingDisplay, fontSize: 15, color: colorLandingInk }}
        >
          {t("landingPage.ecosystem.bookingPage.restaurantName")}
        </Typography>
        <Typography sx={{ color: colorLandingOffer, fontSize: 11, mt: 0.375, mb: 0.625 }}>
          {"★★★★"}
          <Box component="span" sx={{ color: colorLandingLine }}>
            ★
          </Box>
          <Box component="span" sx={{ color: colorLandingMuted, ml: 0.5 }}>
            4.1
          </Box>
        </Typography>

        <Typography
          sx={{ fontFamily: fontLandingDisplay, fontSize: 12, color: colorLandingInk }}
        >
          {t("landingPage.ecosystem.bookingPage.chooseDate")}
        </Typography>
        <Typography sx={{ fontSize: 8, color: colorLandingMuted, mt: 0.25, mb: 0.75 }}>
          {t("landingPage.ecosystem.bookingPage.chooseDateSub")}
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.625, mb: 0.75 }}>
          {legend.map((entry) => (
            <Box
              key={entry.labelKey}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                border: `1px solid ${colorLandingLine}`,
                borderRadius: 999,
                px: 0.875,
                py: 0.375,
                fontSize: 8,
                fontWeight: 700,
                color: colorLandingText,
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: entry.color,
                }}
              />
              {t(entry.labelKey)}
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            border: `1px solid ${colorLandingLine}`,
            borderRadius: "10px",
            p: 0.875,
          }}
        >
          <Typography
            sx={{
              textAlign: "center",
              fontFamily: fontLandingDisplay,
              fontSize: 10.5,
              color: colorLandingInk,
              mb: 0.5,
            }}
          >
            {`‹ ${t("landingPage.ecosystem.bookingPage.month")} ›`}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              fontSize: 7.5,
              color: colorLandingMuted,
              textAlign: "center",
              fontWeight: 700,
              mb: 0.25,
            }}
          >
            {WEEKDAY_INITIALS.map((day) => (
              <Box key={day}>{day}</Box>
            ))}
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "1px",
            }}
          >
            {Array.from({ length: LEADING_BLANKS }).map((_, index) => (
              <Box key={`blank-${index}`} sx={{ aspectRatio: "1" }} />
            ))}
            {Array.from({ length: DAYS_IN_MONTH }, (_, index) => index + 1).map(
              (day) => (
                <Box
                  key={day}
                  sx={{
                    aspectRatio: "1",
                    display: "grid",
                    placeItems: "center",
                    position: "relative",
                    fontSize: 8.5,
                    fontWeight: day === TODAY ? 800 : 600,
                    borderRadius: "4px",
                    color: day === TODAY ? colorLandingSurface : colorLandingText,
                    backgroundColor:
                      day === TODAY ? colorLandingBrand : "transparent",
                  }}
                >
                  {day}
                  {OFFER_DAYS.includes(day) && <DayDot color={colorLandingOffer} />}
                  {EVENT_DAYS.includes(day) && <DayDot color={colorLandingBlue} />}
                </Box>
              )
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
